import { AuthService } from './authService';
import { ItemBinSizeDto, CreateItemBinSizeRequest, UpdateItemBinSizeRequest } from '../types/itemBinSize';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ItemBinSizeService {
  private baseUrl = `${API_BASE_URL}/item-bin-size`;

  async getByCompanyItemAndBinSize(companyNo: string, itemNo: string, binSizeNo: number): Promise<ItemBinSizeDto | null> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${encodeURIComponent(itemNo)}/${binSizeNo}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      if (error.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async getByCompanyNo(companyNo: string): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getByCompanyAndItem(companyNo: string, itemNo: string): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}/item/${encodeURIComponent(itemNo)}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getCurrent(): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getAll(): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async create(request: CreateItemBinSizeRequest): Promise<ItemBinSizeDto> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async update(id: number, request: UpdateItemBinSizeRequest): Promise<ItemBinSizeDto> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${id}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async delete(id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${id}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
  }

  async bulkUpdateForItem(companyNo: string, itemNo: string, itemBinSizes: ItemBinSizeDto[]): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedPut(
      `${this.baseUrl}/bulk/company/${companyNo}/item/${encodeURIComponent(itemNo)}`,
      itemBinSizes
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }
}

export const itemBinSizeService = new ItemBinSizeService();
